import type { ReactNode } from 'react';
import { Check, Eraser, RotateCcw } from 'lucide-react';

/**
 * Editor UI redrawn at card size (1 unit = 1 px of the 256x144 card): the
 * floating tool bars, markers and polygons. Texts are the real ones. The
 * chrome mirrors the editor's black glass (`editor-chrome-glass`, dark kit
 * tokens in kit-next.css) at roughly 0.55 scale: bars 12px -> 7, buttons
 * 12px -> 6, chips 6px -> 3.
 */

export type Pt = readonly [number, number];

export const C = {
  /** Glass base of the floating bars (`--editor-glass`, 87 % over the photo). */
  bar: '#111214',
  /** Secondary button / menu surface (`--color-surface`, dark). */
  surface: '#17191a',
  /** `--color-line-strong`: button borders, rails. */
  edge: 'rgba(255,255,255,.16)',
  /** `--color-line`: separators. */
  line: 'rgba(255,255,255,.09)',
  /** Lit rim of the glass (`--editor-glass-rim`). */
  rim: 'rgba(255,255,255,.08)',
  ink: '#f2f3f1',
  hint: '#b9bdba',
  ink2: '#b9bdba',
  ink3: '#8f9390',
  /** `confirm` button: the dark kit's success green with dark text. */
  green: '#a3d977',
  onGreen: '#111214',
  /** Default marker / polygon colour (user content: unchanged by the redesign). */
  blue: '#2563eb',
  /** The editor's dark `--color-brand` (kit): click ripples, selection. */
  brand: '#3dd6c7',
  cyan: '#22d3ee',
  fuchsia: '#e879f9',
  /** Editor drawing colour (lib/editor-tool-colors.ts): drafts, cuts, handles. */
  tool: '#3dd6c7',
};
export const POLY = 'rgba(37,99,235,.4)';
export const POLY_SEL = 'rgba(37,99,235,.55)';
export const TINTS = ['rgba(37,99,235,.42)', 'rgba(16,185,129,.42)', 'rgba(245,158,11,.44)', 'rgba(236,72,153,.4)'];

const FS = 7.4;
const tw = (s: string, fs = FS) => s.length * fs * 0.54;

/* ---------- tool bars ---------- */

export type Item =
  | { t: 'text'; s: string }
  | { t: 'status'; s: string }
  | { t: 'sep' }
  | { t: 'slider'; label: string; value: string; fill: number; w?: number }
  | {
      t: 'btn';
      s: string;
      icon?: 'undo' | 'eraser' | 'check';
      kbd?: string;
      primary?: boolean;
      ghost?: boolean;
      dim?: boolean;
    };

const GAP = 7;
const BTN_H = 17;

function itemW(it: Item): number {
  switch (it.t) {
    case 'text':
    case 'status':
      return tw(it.s);
    case 'sep':
      return 1;
    case 'slider':
      return Math.max(it.w ?? 0, tw(it.label, 6.6) + tw(it.value, 6.6) + 8);
    case 'btn':
      return 10 + tw(it.s) + (it.icon ? 12 : 0) + (it.kbd ? 10 + tw(it.kbd, 5.6) : 0);
  }
}

const rowW = (row: readonly Item[]) => row.reduce((s, it) => s + itemW(it), 0) + GAP * Math.max(row.length - 1, 0);
const rowH = (row: readonly Item[]) => (row.some(i => i.t === 'slider') ? 22 : BTN_H);

function Icon({ k, x, cy, color }: { k: 'undo' | 'eraser' | 'check'; x: number; cy: number; color: string }) {
  const p = { x, y: cy - 4.5, width: 9, height: 9, stroke: color, strokeWidth: k === 'check' ? 3 : 2.2 };
  return k === 'undo' ? <RotateCcw {...p} /> : k === 'eraser' ? <Eraser {...p} /> : <Check {...p} />;
}

function drawItem(it: Item, x: number, cy: number, w: number): ReactNode {
  switch (it.t) {
    case 'text':
      return (
        <text x={x} y={cy} fontSize={FS} fill={C.hint} dominantBaseline="central">
          {it.s}
        </text>
      );
    case 'status':
      return (
        <text x={x} y={cy} fontSize={FS} fill={C.ink3} dominantBaseline="central">
          {it.s}
        </text>
      );
    case 'sep':
      return <path d={`M${x + 0.5} ${cy - 7} V${cy + 7}`} stroke={C.line} />;
    case 'slider': {
      const tx = x + 1.5;
      const tl = w - 3;
      return (
        <g>
          <text x={x} y={cy - 6.5} fontSize="6.6" fill={C.ink2} dominantBaseline="central">
            {it.label}
          </text>
          <text x={x + w} y={cy - 6.5} fontSize="6.6" fill={C.ink3} textAnchor="end" dominantBaseline="central">
            {it.value}
          </text>
          <rect x={tx} y={cy + 4} width={tl} height="2.4" rx="1.2" fill={C.edge} />
          <rect x={tx} y={cy + 4} width={Math.max(tl * it.fill, 1)} height="2.4" rx="1.2" fill={C.brand} />
          <circle cx={tx + tl * it.fill} cy={cy + 5.2} r="3.2" fill="#ffffff" stroke={C.brand} strokeWidth="1.2" />
        </g>
      );
    }
    case 'btn': {
      // `primary` is the owner-mandated green confirm of the editor tools;
      // `ghost` is the quiet text button; the rest is the kit's secondary.
      const fg = it.primary ? C.onGreen : it.ghost ? C.ink2 : C.ink;
      const tx = x + 5 + (it.icon ? 12 : 0);
      return (
        <g opacity={it.dim ? 0.45 : 1}>
          <rect
            x={x}
            y={cy - BTN_H / 2}
            width={w}
            height={BTN_H}
            rx="6"
            fill={it.primary ? C.green : it.ghost ? 'none' : C.surface}
            stroke={it.primary || it.ghost ? 'none' : C.edge}
          />
          {it.icon && <Icon k={it.icon} x={x + 5} cy={cy} color={fg} />}
          <text
            x={tx}
            y={cy}
            fontSize={FS}
            fontWeight={it.ghost ? 500 : 600}
            fill={fg}
            dominantBaseline="central"
          >
            {it.s}
          </text>
          {it.kbd && (
            <g>
              <rect
                x={tx + tw(it.s) + 5}
                y={cy - 4.6}
                width={tw(it.kbd, 5.6) + 6}
                height="9.2"
                rx="2.6"
                fill={it.primary ? 'rgba(17,18,20,.16)' : 'rgba(255,255,255,.06)'}
                stroke={it.primary ? 'rgba(17,18,20,.3)' : C.edge}
                strokeWidth=".6"
              />
              <text x={tx + tw(it.s) + 8} y={cy} fontSize="5.6" fontWeight="600" fill={it.primary ? C.onGreen : C.ink3} dominantBaseline="central">
                {it.kbd}
              </text>
            </g>
          )}
        </g>
      );
    }
  }
}

/**
 * The editor's black glass (`editor-chrome-glass`): near-black at 87 %, the
 * soft top-left sheen and a lit rim. No blur: it is drawn flat on purpose.
 */
export function Glass({ x, y, width, height, rx = 7, opacity = 0.87 }: { x: number; y: number; width: number; height: number; rx?: number; opacity?: number }) {
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={rx} fill={C.bar} fillOpacity={opacity} />
      <rect x={x} y={y} width={width} height={height} rx={rx} fill="url(#ta-sheen)" />
      <rect x={x + 0.5} y={y + 0.5} width={width - 1} height={height - 1} rx={rx - 0.5} fill="none" stroke={C.rim} />
      <path d={`M${x + rx} ${y + 0.9} H${x + width - rx}`} stroke="#ffffff" strokeOpacity=".12" />
    </g>
  );
}

export interface BarSpec {
  el: ReactNode;
  w: number;
  h: number;
  x: number;
  y: number;
  /** Centre of item `i` of row `r`. */
  at: (r: number, i: number) => Pt;
  /** Left edge and width of item `i` of row `r`, and the row's centre y. */
  span: (r: number, i: number) => readonly [number, number, number];
}

/**
 * A floating bar of the editor. `edge` is `'top'` or `'bottom'` (sticks to that
 * side of the card) and the rows wrap like the real bar does when narrow.
 */
export function makeBar(rows: readonly (readonly Item[])[], edge: 'top' | 'bottom'): BarSpec {
  const PADX = 8;
  const PADY = 4;
  const RGAP = 3;
  const w = Math.max(...rows.map(rowW)) + PADX * 2;
  const h = rows.reduce((s, r) => s + rowH(r), 0) + RGAP * (rows.length - 1) + PADY * 2;
  const x = (256 - w) / 2;
  const y = edge === 'top' ? 5 : 139 - h;
  const centres: Pt[][] = [];
  const spans: (readonly [number, number, number])[][] = [];
  let ry = y + PADY;
  const body = rows.map((row, r) => {
    const rh = rowH(row);
    const cy = ry + rh / 2;
    ry += rh + RGAP;
    let ix = x + PADX;
    centres[r] = [];
    spans[r] = [];
    return (
      <g key={r}>
        {row.map((it, i) => {
          const iw = itemW(it);
          const node = drawItem(it, ix, cy, iw);
          centres[r]![i] = [ix + iw / 2, cy];
          spans[r]![i] = [ix, iw, cy];
          ix += iw + GAP;
          return <g key={i}>{node}</g>;
        })}
      </g>
    );
  });
  return {
    w,
    h,
    x,
    y,
    at: (r, i) => centres[r]?.[i] ?? [128, 72],
    span: (r, i) => spans[r]?.[i] ?? [0, 0, 0],
    el: (
      <g filter="url(#ta-pop)">
        <Glass x={x} y={y} width={w} height={h} rx={7} />
        {body}
      </g>
    ),
  };
}

/* ---------- markers ---------- */

/** A default point: tour-blue disc with a white border (22 px in the editor). */
export function Dot({ x, y }: { x: number; y: number }) {
  return <circle cx={x} cy={y} r="7" fill={C.blue} stroke="#ffffff" strokeWidth="2.6" />;
}

/** The selection ring: white, with a faint dark halo so it reads on a bright photo. */
export function SelRing({ x, y, r = 12 }: { x: number; y: number; r?: number }) {
  return (
    <g fill="none">
      <circle cx={x} cy={y} r={r} stroke="#ffffff" strokeWidth="1.6" />
      <circle cx={x} cy={y} r={r + 1.2} stroke="#000000" strokeOpacity=".25" strokeWidth=".8" />
    </g>
  );
}

/** Same ring around a rectangle (label, image). */
export function BoxRing({ x, y, w, h, r = 5 }: { x: number; y: number; w: number; h: number; r?: number }) {
  return (
    <g fill="none">
      <rect x={x - 3} y={y - 3} width={w + 6} height={h + 6} rx={r + 3} stroke="#ffffff" strokeWidth="1.6" />
      <rect x={x - 4.2} y={y - 4.2} width={w + 8.4} height={h + 8.4} rx={r + 4.2} stroke="#000000" strokeOpacity=".25" strokeWidth=".8" />
    </g>
  );
}

/** A point that goes to another image: chevron, with the destination's name below. */
export function LinkDot({ x, y, name }: { x: number; y: number; name: string }) {
  return (
    <g>
      <circle cx={x} cy={y} r="10" fill={C.blue} stroke="#ffffff" strokeWidth="2.4" />
      <path d={`M${x - 4.5} ${y + 2.5} L${x} ${y - 2} L${x + 4.5} ${y + 2.5}`} fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <text x={x} y={y + 22} textAnchor="middle" fontSize="8.5" fontWeight="600" fill="#ffffff" filter="url(#ta-lbl)">
        {name}
      </text>
    </g>
  );
}

export const path = (pts: readonly Pt[]) => `M${pts.map(([x, y]) => `${x} ${y}`).join(' L')} Z`;

export function Poly({ pts, sel, fill }: { pts: readonly Pt[]; sel?: boolean; fill?: string }) {
  return (
    <path
      d={path(pts)}
      fill={fill ?? (sel ? POLY_SEL : POLY)}
      stroke={sel ? '#ffffff' : C.blue}
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  );
}

/**
 * A polygon being drawn, as the editor renders its draft (use-marker-interactions.ts,
 * `__draft_polygon__`): from 3 vertices on, a closed outline (the closing edge
 * included) with a dashed stroke and a light fill. `sw` scales stroke and dashes.
 */
export function DraftPoly({ pts, sw = 1.3 }: { pts: readonly Pt[]; sw?: number }) {
  return (
    <path
      d={path(pts)}
      fill="rgba(61,214,199,.2)"
      stroke={C.tool}
      strokeWidth={sw}
      strokeDasharray={`${sw * 2} ${sw * 2}`}
      strokeLinejoin="round"
    />
  );
}

/** White handles on the vertices of a selected polygon. */
export function Handles({ pts }: { pts: readonly Pt[] }) {
  return (
    <g fill="#ffffff" stroke={C.tool} strokeWidth="1">
      {pts.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="3" />
      ))}
    </g>
  );
}

/** Vertex of a polygon being drawn. */
export function DraftVertex({ x, y }: { x: number; y: number }) {
  return <circle cx={x} cy={y} r="3.4" fill={C.tool} stroke="#ffffff" strokeWidth="1.4" />;
}

/** Handle of a cut point. */
export function CutHandle({ x, y }: { x: number; y: number }) {
  return <circle cx={x} cy={y} r="3.2" fill={C.tool} stroke="#111827" strokeWidth=".9" />;
}

